import * as React from "react";
import { toast } from "react-toastify";
import { CellContext } from "@tanstack/react-table";

import { FilterParams } from "src/library/utils";
import { ActionButton } from "src/library/components/Button";
import { LikeToDo } from "src/library/components/LikeToDo";
import { ButtonLink } from "src/library/components/Link";
import { Modal } from "src/library/components/Modal";
import { ITableInstance } from "src/library/components/Tables/Table";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PaginatedParams } from "src/core/shared/api";
import {
  IUser,
  useUserApi
} from "src/core/users/api/users.api";
import { PageProps } from "src/pages/Router/routes";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { getUserTableColumns } from "src/core/shared/components/tables/usersTableColumns";
import { Table } from "src/core/dashboard/components/widgets";

const defaultPagination: PaginatedParams<IUser> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "firstName", "ASC" ] ]
};

/**
 * Main "Users" View
 *
 * @returns
 */
const UsersPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { terminology, history } = props;
  const TableRef = React.useRef<ITableInstance<IUser>>(null);
  const [ usersResponse, getUsers ] = useUserApi("LIST_USERS");
  const [ deleteUserResponse, deleteUser ] = useUserApi("DELETE_USER");
  const [ suspendUserResponse, suspendUser ] = useUserApi("UPDATE_USER");
  const [ resendInviteResponse, resendInvite ] = useUserApi("RESEND_INVITE");
  const [ modalData, setModalData ] = React.useState<number | null>(null);
  const checkPermission = useCheckUserPermission();

  const canCreateUsers = React.useMemo(() => {
    return checkPermission("create-user");
  }, [ checkPermission ]);

  const onFetchData = React.useCallback((params: {filters: FilterParams<IUser>, pagination: PaginatedParams<IUser>}) => {
    getUsers({
      pagination: params.pagination,
      filters: {
        ...params.filters,
        defaultCustomerId: null
      }
    });
  }, [ getUsers ]);

  const CreateNewUserButton = (
    <ButtonLink
      background="primaryDark"
      to="/admin/users/create"
    >
      {`Create a new admin ${terminology.user.singular.text}`}
    </ButtonLink>
  );

  const editUserAction = React.useCallback((id: number) => {
    history.push(`/admin/users/${id}`);
  }, [ history ]);

  const deleteUserAction = (id: number) => {
    setModalData(id);
  };

  const resendInviteUserAction = React.useCallback((id: number) => {
    resendInvite({ id });
  }, [ resendInvite ]);

  const deleteUserActionConfirm = () => {
    if (modalData) {
      deleteUser({ id: modalData });
      setModalData(null);
    }
  };

  const handleSuspendUser = React.useCallback((user: any) => {
    const updatedUser = {
      ...user,
      status: user.status === "active" ? "suspended" : "active"
    };

    suspendUser(updatedUser);
  }, [ suspendUser ]);

  // On user update
  React.useEffect(() => {
    if (suspendUserResponse.data) {
      toast.success(`The ${terminology.user.singular.text} has been successfully updated.`);
    }

    if (suspendUserResponse.error) {
      toast.error(`Could not update ${terminology.user.singular.text} - ${suspendUserResponse.error.message}`);
    }
  }, [ suspendUserResponse ]);

  React.useEffect(() => {
    if (resendInviteResponse.data) {
      toast.success("The invite has been sent");
    }

    if (resendInviteResponse.error) {
      toast.error(`Invite not sent ${terminology.user.singular.text} - ${resendInviteResponse.error.message}`);
    }
  }, [ resendInviteResponse ]);

  // On user deletion
  React.useEffect(() => {
    if (deleteUserResponse.data) {
      toast.success(`The ${terminology.user.singular.text} has been successfully deleted.`);

      if (TableRef.current) {
        TableRef.current.reloadTable(true);
      }
    }

    if (deleteUserResponse.error) {
      toast.error(`Could not delete ${terminology.user.singular.text} - ${deleteUserResponse.error.message}`);
    }
  }, [ deleteUserResponse ]);

  const userTableActionsButton = React.useCallback((info: CellContext<IUser, unknown>) => {
    const canAmendUser = checkPermission("amend-user");

    if (!canAmendUser) {
      return null;
    }

    const items = [
      {
        title: "Edit",
        action: () => editUserAction(info.row.original.id)
      },
      {
        title: `${info.row.original.status === "active" ? "Suspend" : "Activate"}`,
        action: () => {
          handleSuspendUser(info.row.original);
        }
      },
      {
        title: "Resend invite",
        action: () => resendInviteUserAction(info.row.original.id)
      },
      {
        title: "Delete",
        action: () => deleteUserAction(info.row.original.id)
      }
    ];

    return (
      <ActionButton
        title="Actions"
        items={items}
      />
    );
  }, [
    checkPermission,
    editUserAction,
    handleSuspendUser,
    resendInviteUserAction
  ]);

  const columns = React.useMemo(() => {
    return getUserTableColumns({
      terminology,
      actionButton: userTableActionsButton,
      removeColumns: [ "defaultCustomer", "defaultDrop" ]

    });
  }, [ terminology, userTableActionsButton ]);

  return (
    <Layout
      {...props}
      title={`Admin ${terminology.user.plural.text}`}
    >
      <MainContentWrapper
        fill
        Header={canCreateUsers && (
          <LikeToDo
            copy="What would you like to do?"
            buttons={[ CreateNewUserButton ]}
          />
        )}
      >

        <Table
          ref={TableRef}
          mainAction={row => editUserAction(row.original.id)}
          error={usersResponse.error}
          tableOf={terminology.user.plural.text}
          loading={usersResponse.pending}
          data={usersResponse.data?.result ? usersResponse.data?.result : []}
          onFetchData={onFetchData}
          rowCount={usersResponse?.data?.count ?? 0}
          pageCount={usersResponse.data?.pages ?? 0}
          defaultPagination={defaultPagination}
          columns={columns}
        />
      </MainContentWrapper>

      <Modal
        small
        show={modalData !== null}
        type="confirm"
        title="Are you sure?"
        body="Deleting this user cannot be undone."
        backgroundAction={() => setModalData(null)}
        actions={[
          {
            name: "No, cancel",
            action: () => setModalData(null),
            $buttonType: "secondary"
          },
          {
            name: "I confirm",
            action: deleteUserActionConfirm,
            $buttonType: "primary"
          }
        ]}
      />
    </Layout>
  );
};

export default UsersPage;
